/**
 * Throttle utility for scroll and resize handlers.
 * Limits function execution to max 60fps for performance.
 * 
 * Based on ENHANCED_SYSTEM_PROMPT.md performance requirements.
 */

/**
 * Creates a throttled version of a function that only executes
 * at most once per specified time period.
 * 
 * @param func - The function to throttle
 * @param wait - Minimum time between executions in ms (default: 16ms for 60fps)
 * @returns Throttled function
 * 
 * @example
 * ```ts
 * const handleScroll = throttle(() => {
 *   updateScrollProgress();
 * }, 16);
 * 
 * window.addEventListener('scroll', handleScroll);
 * ```
 */
export function throttle<T extends (...args: unknown[]) => void>(
    func: T,
    wait: number = 16
): T {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    let lastRan: number | null = null;

    return function (this: unknown, ...args: Parameters<T>) {
        if (!lastRan) {
            func.apply(this, args);
            lastRan = Date.now();
        } else {
            if (timeout) clearTimeout(timeout);

            timeout = setTimeout(() => {
                if (Date.now() - (lastRan as number) >= wait) {
                    func.apply(this, args);
                    lastRan = Date.now();
                }
            }, wait - (Date.now() - (lastRan as number)));
        }
    } as T;
}

/**
 * Creates a debounced version of a function that only executes
 * after a specified delay since the last call.
 * 
 * @param func - The function to debounce
 * @param wait - Delay before execution in ms
 * @returns Debounced function
 */
export function debounce<T extends (...args: unknown[]) => void>(
    func: T,
    wait: number
): T {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    return function (this: unknown, ...args: Parameters<T>) {
        if (timeout) clearTimeout(timeout);

        timeout = setTimeout(() => {
            func.apply(this, args);
        }, wait);
    } as T;
}

/**
 * Request animation frame-based throttle for smoothest performance.
 * Useful for scroll/resize handlers that need visual updates.
 */
export function rafThrottle<T extends (...args: unknown[]) => void>(func: T): T {
    let rafId: number | null = null;
    let lastArgs: Parameters<T> | null = null;

    return function (this: unknown, ...args: Parameters<T>) {
        lastArgs = args;

        if (rafId === null) {
            rafId = requestAnimationFrame(() => {
                rafId = null;
                if (lastArgs) {
                    func.apply(this, lastArgs);
                }
            });
        }
    } as T;
}

export default throttle;
